import { esRechazoPorTasa } from 'src/app/shared/utils/error-http-real';
import { ahorroFrenteABase } from '../home-rates/rules/home-rates.rules';
import { montoEnUsdtParaTramo } from 'src/app/shared/utils/monto-usdt-tramo';

/**
 * Tramos de CLP→USDT (compra) y USDT→CLP (venta), 22/10/2026. Funciones puras,
 * sin Angular, para que el cálculo se pruebe solo y las pantallas nunca
 * dependan de que la llamada de tramos responda.
 *
 * Mismas reglas que el back (`tramos-clp-usdt.service.ts` en la API):
 *   - compra: el tramo se elige con los USDT a la BASE (`montoClp ÷ base`; con
 *     `precioCorte` válido en el GET, 23/10, `montoClp ÷ precioCorte`) y
 *     se cobra `min(tramo, segmentación')`, con `segmentación' = max(seg,
 *     pisoSegmentacion)`: el servidor recorta la segmentación al piso de ida y
 *     vuelta y lo publica en el mismo GET (`pisoSegmentacion`). No rechaza una
 *     segmentación bajo el piso: la liquida al piso; por eso la pantalla
 *     cobra lo mismo de entrada. Sin el campo (backend viejo), sin recorte,
 *     como antes;
 *   - venta: el tramo se elige directo con los USDT vendidos;
 *   - en los dos, el tramo es el más alto cuyo «desde» no supera el monto;
 *     bajo el primero, el primero.
 *
 * La tasa es CLP por USDT. En la compra una tasa más BAJA es mejor para el
 * cliente; en la venta, una más ALTA.
 *
 * «No romper»: si la llamada de tramos falla (backend viejo, 404, 503) la
 * pantalla hace EXACTAMENTE lo de hoy. Estas funciones solo se usan cuando la
 * lectura fue buena.
 */

/** Un tramo tal como lo ve el cliente (sin costo ni margen). */
export interface TramoClp {
  clave?: string;
  orden?: number;
  desde: number;
  hasta?: number;
  /** CLP por USDT. */
  tasa: number;
  mejoraPct?: number;
}

/** Respuesta de `rates/tramos-usdt/clp-usdt` o `usdt-clp` (los campos que se usan). */
export interface TramosClpData {
  base?: number | null;
  /** Solo la compra: `valores_cambio.id` del snapshot. */
  baseId?: number | string | null;
  /** Solo la compra: piso de la segmentación (`seg' = max(seg, piso)`). Null = sin recorte. */
  pisoSegmentacion?: number | null;
  tramos?: TramoClp[] | null;
  /**
   * Aditivo (23/10): precio del USDT con que el servidor elige el tramo de un
   * monto en CLP (`montoClp ÷ precioCorte`, el spot). Null/ausente = `÷ base`.
   */
  precioCorte?: number | null;
}

/** Diferencia de tasa (CLP) por debajo de la cual se considera la misma. */
export const TOLERANCIA_TASA_CLP = 0.005;

const round2 = (n: number): number => Math.round(n * 100) / 100;

const positivo = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Tramos usables: con tasa y «desde» válidos, ordenados por «desde». */
export function tramosValidosClp(data: TramosClpData | null | undefined): TramoClp[] {
  const lista = Array.isArray(data?.tramos) ? (data!.tramos as TramoClp[]) : [];
  return lista
    .filter((t) => !!t && positivo(t.tasa) !== null && Number.isFinite(Number(t.desde)))
    .sort((a, b) => Number(a.desde) - Number(b.desde));
}

/**
 * Tramo de un monto en USDT: el más alto cuyo «desde» no lo supera. Bajo el
 * primero, el primero. Null si no hay tramos o el monto no es un número.
 */
export function tramoDelMontoClp<T extends { desde: number }>(tramos: T[], montoUsdt: number): T | null {
  if (!tramos?.length || !Number.isFinite(Number(montoUsdt))) return null;
  let elegido = tramos[0];
  for (const t of tramos) if (Number(montoUsdt) >= Number(t.desde)) elegido = t;
  return elegido;
}

// ───────────────────────── Compra CLP→USDT ─────────────────────────

/**
 * Mejor tasa de compra (la más baja) entre la base y los tramos, para el
 * «Desde X». Null si ningún tramo mejora la base: no hay nada que anunciar.
 */
export function mejorTasaCompra(data: TramosClpData | null | undefined, base: number): number | null {
  const b = positivo(base);
  if (b === null) return null;
  const tramos = tramosValidosClp(data);
  if (!tramos.length) return null;
  const min = Math.min(...tramos.map((t) => Number(t.tasa)));
  return b - min > TOLERANCIA_TASA_CLP ? min : null;
}

/**
 * CLP que ahorra el cliente al pagar `montoClp` a `tasa` en vez de a la base:
 * lo que le habrían costado los mismos USDT a la base, menos lo que paga.
 * `montoClp × (base ÷ tasa − 1)`, en pesos enteros hacia abajo. 0 si no mejora.
 *
 * La cuenta es una sola, la de la escalera (`ahorroFrenteABase`, PAGA_MENOS);
 * acá solo se le agregan la tolerancia y el redondeo a pesos.
 */
export function ahorroCompraClp(montoClp: number, base: number, tasa: number): number {
  const m = positivo(montoClp);
  const b = positivo(base);
  const t = positivo(tasa);
  if (m === null || b === null || t === null || !(b - t > TOLERANCIA_TASA_CLP)) return 0;
  return Math.floor(ahorroFrenteABase(m, t, b, 'PAGA_MENOS', 'CLP', 'USDT').valor + 1e-9);
}

/**
 * Tramos de compra que valen para la fila base de la pantalla. Vacío si el
 * snapshot es de OTRA fila (`baseId ≠ valorCambioId`): sus tasas no son las
 * que va a cobrar el servidor. Lo usan la cotización y la escalera.
 */
export function tramosCompraVigentes(
  data: TramosClpData | null | undefined,
  valorCambioId?: number | string | null,
): TramoClp[] {
  const otraFila =
    data?.baseId != null &&
    valorCambioId != null &&
    String(valorCambioId) !== '0' &&
    String(data.baseId) !== String(valorCambioId);
  return otraFila ? [] : tramosValidosClp(data);
}

export interface CotizacionCompraClp {
  /** Tasa a cobrar: `min(tramo o base, max(segmentación, pisoSegmentacion))`. */
  tasa: number;
  /** Tramo del monto, solo si mejora la base; null si cobra la base. */
  tramo: TramoClp | null;
  /** Mejor tasa publicada («Desde X»); null si ningún tramo mejora la base. */
  desde: number | null;
  /** CLP ahorrados frente a la base con este monto (0 si no hay tramo que mejore). */
  ahorroClp: number;
}

/**
 * Tasa de compra para `montoClp` con tramos. `base` es la tasa de la pantalla
 * (`valores_cambio.tasa_usdt_clp`, id `valorCambioId`) y `segmentada` la que
 * calcula hoy el front (null si no aplica), recortada al `pisoSegmentacion`
 * del GET como en el servidor; el ahorro se cuenta con la tasa que queda.
 * Si el snapshot de tramos es de
 * OTRA fila base (`baseId ≠ valorCambioId`), sus tasas no son las que va a
 * cobrar el servidor para esa fila: se cobra la base (el back siempre la
 * acepta en la compra, una tasa más alta solo perjudica al cliente).
 */
export function cotizarCompraClp(p: {
  data: TramosClpData | null | undefined;
  montoClp: number;
  base: number;
  valorCambioId?: number | string | null;
  segmentada?: number | null;
}): CotizacionCompraClp {
  const base = Number(p.base);
  const segCruda = positivo(p.segmentada);
  const piso = positivo(p.data?.pisoSegmentacion);
  // seg' = max(seg, piso), el mismo recorte del servidor. Sin piso, como antes.
  const seg = segCruda === null ? null : piso === null ? segCruda : Math.max(segCruda, piso);
  const conSeg = (t: number): number => (seg === null ? t : Math.min(t, seg));
  try {
    if (!(base > 0)) return { tasa: base, tramo: null, desde: null, ahorroClp: 0 };
    const tramos = tramosCompraVigentes(p.data, p.valorCambioId);
    const otraFila = !tramos.length && tramosValidosClp(p.data).length > 0;
    const monto = Number(p.montoClp) || 0;
    const t = tramoDelMontoClp(tramos, montoEnUsdtParaTramo(monto, p.data?.precioCorte, base));
    const mejora = t && base - Number(t.tasa) > TOLERANCIA_TASA_CLP ? t : null;
    const tasa = conSeg(mejora ? Number(mejora.tasa) : base);
    return {
      tasa,
      tramo: mejora,
      desde: otraFila ? null : mejorTasaCompra(p.data, base),
      ahorroClp: mejora ? ahorroCompraClp(monto, base, tasa) : 0,
    };
  } catch {
    return { tasa: conSeg(base), tramo: null, desde: null, ahorroClp: 0 };
  }
}

// ───────────────────────── Compra escrita en USDT ─────────────────────────

export interface CotizacionDesdeUsdt {
  /** Pesos a pagar: lo que se emite y viaja en el body. */
  clp: number;
  /** USDT que se acreditan con esa tasa: los escritos o, si hubo que subir al tramo, algo más. */
  usdt: number;
  /** CLP por USDT que cobra el servidor por `clp`. */
  tasa: number;
  /** No había tasa que diera justo los USDT escritos: se subió al primer peso del tramo. */
  subido: boolean;
}

/**
 * Compra CLP→USDT con el monto escrito en USDT. El servidor elige el tramo
 * con los PESOS (`clp ÷ base`), no con los USDT, así que `clp = usdt ×
 * tasaDelTramo` puede caer en el tramo anterior y acreditar menos de lo que
 * se ve (1001 USDT → 1000,81).
 *
 *  1. Punto fijo: una tasa `t` tal que la tasa que cobra el servidor por
 *     `usdt × t` pesos sea `t`. Si hay varias, la más barata.
 *  2. Si no hay (los USDT caen en el salto entre dos tramos), el primer corte
 *     en pesos que acredita al menos esos USDT: `ceil(desde × base)` ajustado
 *     al peso (`montoParaCorte`). El cliente recibe un poco MÁS, nunca menos.
 *
 * `tasaDe(clp)` es la tasa que cobra la pantalla (la misma cuenta del
 * servidor) y `cortes`, los pesos donde la tasa puede cambiar (cada tramo y
 * el umbral de la segmentación). Null si no hay monto o nada cierra: la
 * calculadora hace lo de hoy.
 */
export function cotizarDesdeUsdt(
  usdt: number,
  tasaDe: (clp: number) => number,
  cortes: number[],
): CotizacionDesdeUsdt | null {
  const u = Number(usdt);
  if (!(u > 0)) return null;
  const puntos = Array.from(new Set([1, ...cortes.filter((c) => Number.isFinite(c) && c > 0)])).sort(
    (a, b) => a - b,
  );
  const candidatas = Array.from(new Set(puntos.map((p) => tasaDe(p)).filter((t) => t > 0))).sort(
    (a, b) => a - b,
  );
  for (const t of candidatas) {
    const clp = u * t;
    if (Math.abs(tasaDe(clp) - t) < 1e-9) return { clp, usdt: u, tasa: t, subido: false };
  }
  for (const b of puntos) {
    const t = tasaDe(b);
    if (t > 0 && b / t >= u) return { clp: b, usdt: b / t, tasa: t, subido: true };
  }
  return null;
}

// ───────────────────────── Venta USDT→CLP ─────────────────────────

/**
 * Mejor tasa de venta (la más alta) entre la base y los tramos, para el
 * «Desde X». Null si ningún tramo mejora la base.
 */
export function mejorTasaVenta(data: TramosClpData | null | undefined, base: number): number | null {
  const b = positivo(base);
  if (b === null) return null;
  const tramos = tramosValidosClp(data);
  if (!tramos.length) return null;
  const max = Math.max(...tramos.map((t) => Number(t.tasa)));
  return max - b > TOLERANCIA_TASA_CLP ? max : null;
}

export interface CotizacionVentaClp {
  /** Tasa a pagar (CLP por USDT). Sin tramo que mejore, la base. */
  tasa: number;
  tramo: TramoClp | null;
  /** Mejor tasa publicada («Desde X»); null si ningún tramo mejora la base. */
  desde: number | null;
  /** CLP de más frente a vender los mismos USDT a la base, en pesos enteros hacia abajo. */
  extraClp: number;
}

/**
 * Tasa de venta para `montoUsdt` con tramos. Un tramo que no supera la base
 * (el chico, o datos desfasados) no cambia nada: se paga la base, la de
 * siempre. El back acepta hasta la tasa del tramo (+0,5 % en la conversión,
 * ±1 % en el retiro VE) y liquida con la suya.
 */
export function cotizarVentaClp(p: {
  data: TramosClpData | null | undefined;
  montoUsdt: number;
  base: number;
}): CotizacionVentaClp {
  const base = Number(p.base);
  try {
    if (!(base > 0)) return { tasa: base, tramo: null, desde: null, extraClp: 0 };
    const tramos = tramosValidosClp(p.data);
    const usdt = Number(p.montoUsdt) || 0;
    const t = tramoDelMontoClp(tramos, usdt);
    const mejora = t && Number(t.tasa) - base > TOLERANCIA_TASA_CLP ? t : null;
    const tasa = mejora ? Number(mejora.tasa) : base;
    return {
      tasa,
      tramo: mejora,
      desde: mejorTasaVenta(p.data, base),
      // Misma cuenta que la escalera (RECIBE_MAS: `usdt × (tasa − base)`).
      extraClp:
        mejora && usdt > 0
          ? Math.floor(round2(ahorroFrenteABase(usdt, tasa, base, 'RECIBE_MAS', 'USDT', 'CLP').valor) + 1e-9)
          : 0,
    };
  } catch {
    return { tasa: base, tramo: null, desde: null, extraClp: 0 };
  }
}

// ───────────────────────── Errores de las conversiones ─────────────────────────

/**
 * Qué hacer con el error de una conversión CLP↔USDT (ya traducido por
 * `errorHttpReal`, `{ status, message, transitorio }`):
 *   - `TASA_CAMBIO`: 400/409 «La tasa cambió…». No se hizo nada: se recotiza
 *     y se avisa.
 *   - `SIN_TASA`: 503. El back no tiene tasa (lo responde ANTES de mover
 *     nada) o el balanceador no llegó al servicio: no es incierto.
 *   - `INCIERTO`: sin conexión, timeout o 5xx. Pudo haberse hecho: no se
 *     ofrece reintentar.
 *   - `RECHAZO`: cualquier otro 4xx. Se muestra el mensaje del servidor.
 */
export type TipoErrorConversionClp = 'TASA_CAMBIO' | 'SIN_TASA' | 'INCIERTO' | 'RECHAZO';

export const TEXTO_SIN_TASA_CLP =
  'En este momento no hay una tasa disponible. Intenta nuevamente en unos minutos.';

export const TEXTO_TASA_CAMBIO_CLP =
  'La tasa cambió mientras confirmabas. Ya actualizamos la calculadora: revisa el nuevo monto y vuelve a confirmar.';

export function clasificarErrorConversionClp(
  e: { status?: number; message?: string; transitorio?: boolean } | null | undefined,
): TipoErrorConversionClp {
  if (e?.status === 503) return 'SIN_TASA';
  if (esRechazoPorTasa(e?.status, e?.message)) return 'TASA_CAMBIO';
  if (e?.transitorio === true) return 'INCIERTO';
  return 'RECHAZO';
}
